import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, X } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip } from 'recharts';
import { differenceInMinutes, differenceInHours } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type { Device } from '@/lib/traccar-api';

export type CommunicationFilter = 'recent' | 'warning' | 'critical' | 'noposition' | null;

interface LastCommunicationCardProps {
  devices: Device[];
  activeFilter?: CommunicationFilter;
  onFilterChange?: (filter: CommunicationFilter) => void;
}

interface CommunicationRange {
  label: string;
  count: number;
  color: string;
  fillColor: string;
  filter: CommunicationFilter;
}

export const LastCommunicationCard = ({ devices, activeFilter, onFilterChange }: LastCommunicationCardProps) => {
  const { t } = useTranslation();
  const now = new Date();

  const ranges: CommunicationRange[] = [
    { label: '< 5 min', count: 0, color: 'bg-[hsl(142,76%,36%)]', fillColor: 'hsl(142,76%,36%)', filter: 'recent' },
    { label: '5 min - 24h', count: 0, color: 'bg-[hsl(142,76%,50%)]', fillColor: 'hsl(142,76%,50%)', filter: 'recent' },
//    { label: '1-24 hours', count: 0, color: 'bg-[hsl(45,93%,47%)]', fillColor: 'hsl(45,93%,47%)', filter: 'warning' },
    { label: '> 24 hours', count: 0, color: 'bg-[hsl(0,84%,60%)]', fillColor: 'hsl(0,84%,60%)', filter: 'critical' },
    { label: t('settings.session.never'), count: 0, color: 'bg-[hsl(220,13%,50%)]', fillColor: 'hsl(220,13%,50%)', filter: 'noposition' },
  ];

  devices.forEach(device => {
    if (!device.lastUpdate) {
      ranges[3].count++;
      return;
    }

    const lastUpdate = new Date(device.lastUpdate);
    const minutesAgo = differenceInMinutes(now, lastUpdate);
    const hoursAgo = differenceInHours(now, lastUpdate);

    if (minutesAgo < 5) {
      ranges[0].count++;
    } else if (hoursAgo < 24) {
      ranges[1].count++;
    } else {
      ranges[2].count++;
    }
  });

  const pieData = ranges.filter(d => d.count > 0).map(r => ({
    name: r.label,
    value: r.count,
    fillColor: r.fillColor,
    filter: r.filter,
  }));

  const totalDevices = devices.length;

  const handlePieClick = (filter: CommunicationFilter) => {
    if (onFilterChange) {
      onFilterChange(activeFilter === filter ? null : filter);
    }
  };

  const clearFilter = () => {
    if (onFilterChange) {
      onFilterChange(null);
    }
  };

  const getRangeOpacity = (range: CommunicationRange) => {
    if (!activeFilter) return 1;
    return range.filter === activeFilter ? 1 : 0.4;
  };

  return (
    <Card className="h-full flex flex-col overflow-hidden">
      <CardHeader className="py-2 pl-4 pr-14 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-primary/10">
              <Clock className="h-3.5 w-3.5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-sm">{t('dashboard.lastUpdate')}</CardTitle>
            </div>
          </div>
          {activeFilter && (
            <Badge
              variant="secondary"
              className="gap-1 cursor-pointer hover:bg-destructive hover:text-destructive-foreground transition-colors text-xs"
              onClick={clearFilter}
            >
              {activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1)}
              <X className="h-3 w-3" />
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="py-2 px-4 flex-1 min-h-0">
        <div className="flex items-center gap-3 h-full">
          <div className="relative flex-shrink-0">
            <PieChart width={100} height={100}>
              <Pie
                data={pieData}
                cx={45}
                cy={45}
                innerRadius={30}
                outerRadius={45}
                paddingAngle={2}
                dataKey="value"
                style={{ cursor: 'pointer' }}
                onClick={(_, index) => handlePieClick(pieData[index].filter)}
              >
                {pieData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.fillColor}
                    opacity={activeFilter && activeFilter !== entry.filter ? 0.3 : 1}
                    stroke={activeFilter === entry.filter ? 'hsl(var(--foreground))' : 'none'}
                    strokeWidth={activeFilter === entry.filter ? 2 : 0}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
              />
            </PieChart>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <span className="text-lg font-bold">
                  {activeFilter ? pieData.find(d => d.filter === activeFilter)?.value ?? 0 : totalDevices}
                </span>
                <span className="text-[10px] text-muted-foreground block">
                  {activeFilter ?? 'total'}
                </span>
              </div>
            </div>
          </div>
          <div className="flex-1 space-y-1 overflow-hidden">
            {ranges.map((range) => {
              const percentage = totalDevices > 0 ? Math.round((range.count / totalDevices) * 100) : 0;
              return (
                <button
                  key={range.label}
                  onClick={() => handlePieClick(range.filter)}
                  className={`w-full flex items-center gap-2 text-xs py-0.5 px-1 rounded hover:bg-muted/50 transition-colors`}
                  style={{ opacity: getRangeOpacity(range) }}
                >
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${range.color}`} />
                  <span className="text-muted-foreground truncate flex-1 text-left">{range.label}</span>
                  <span className="font-medium">{range.count} ({percentage}%)</span>
                </button>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
